import { databases, DATABASE_ID, COLLECTIONS, Query, ID } from '../lib/appwrite';

export const settingsService = {
  // Get user settings
  async getUserSettings(userId) {
    try {
      // Try to get existing settings
      const response = await databases.listDocuments(
        DATABASE_ID,
        COLLECTIONS.USER_SETTINGS,
        [
          Query.equal('user_id', userId),
          Query.limit(1)
        ]
      );

      if (response.documents.length === 0) {
        // If settings don't exist, create them with defaults
        return await this.createUserSettings(userId);
      }

      const data = response.documents[0];

      // Convert snake_case to camelCase
      return {
        id: data.$id,
        userId: data.user_id,
        emailNotifications: data.email_notifications,
        temperatureAlerts: data.temperature_alerts,
        sampleMovementAlerts: data.sample_movement_alerts,
        sessionTimeout: data.session_timeout,
        ipWhitelist: data.ip_whitelist,
        autoBackup: data.auto_backup,
        dataRetentionPolicy: data.data_retention_policy,
        exportData: data.export_data,
        darkMode: data.dark_mode,
        compactView: data.compact_view,
        showGridLines: data.show_grid_lines,
        createdAt: data.$createdAt,
        updatedAt: data.$updatedAt
      };
    } catch (error) {
      console.error('Error getting user settings:', error);
      throw error;
    }
  },

  // Create default user settings
  async createUserSettings(userId) {
    const data = await databases.createDocument(
      DATABASE_ID,
      COLLECTIONS.USER_SETTINGS,
      ID.unique(),
      {
        user_id: userId,
        email_notifications: true,
        temperature_alerts: true,
        sample_movement_alerts: false,
        session_timeout: true,
        ip_whitelist: false,
        auto_backup: true,
        data_retention_policy: true,
        export_data: true,
        dark_mode: false,
        compact_view: false,
        show_grid_lines: true
      }
    );

    return {
      id: data.$id,
      userId: data.user_id,
      emailNotifications: data.email_notifications,
      temperatureAlerts: data.temperature_alerts,
      sampleMovementAlerts: data.sample_movement_alerts,
      sessionTimeout: data.session_timeout,
      ipWhitelist: data.ip_whitelist,
      autoBackup: data.auto_backup,
      dataRetentionPolicy: data.data_retention_policy,
      exportData: data.export_data,
      darkMode: data.dark_mode,
      compactView: data.compact_view,
      showGridLines: data.show_grid_lines,
      createdAt: data.$createdAt,
      updatedAt: data.$updatedAt
    };
  },

  // Update specific setting
  async updateSetting(userId, settingKey, value) {
    // Convert camelCase to snake_case
    const snakeCaseKey = settingKey.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);

    // Find the settings document
    const response = await databases.listDocuments(
      DATABASE_ID,
      COLLECTIONS.USER_SETTINGS,
      [
        Query.equal('user_id', userId),
        Query.limit(1)
      ]
    );

    if (response.documents.length === 0) {
      throw new Error('User settings not found');
    }

    const settingsDoc = response.documents[0];

    // Update the setting
    const data = await databases.updateDocument(
      DATABASE_ID,
      COLLECTIONS.USER_SETTINGS,
      settingsDoc.$id,
      { [snakeCaseKey]: value }
    );

    return {
      id: data.$id,
      userId: data.user_id,
      emailNotifications: data.email_notifications,
      temperatureAlerts: data.temperature_alerts,
      sampleMovementAlerts: data.sample_movement_alerts,
      sessionTimeout: data.session_timeout,
      ipWhitelist: data.ip_whitelist,
      autoBackup: data.auto_backup,
      dataRetentionPolicy: data.data_retention_policy,
      exportData: data.export_data,
      darkMode: data.dark_mode,
      compactView: data.compact_view,
      showGridLines: data.show_grid_lines,
      createdAt: data.$createdAt,
      updatedAt: data.$updatedAt
    };
  }
};
